import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Ban, CheckCircle2, ChevronLeft, ChevronRight, Pencil, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { UnifiedTable, UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import { YesNoDialog } from '@/components/ui/modules/yes-no-dialog';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';

const LIST_EFAX_RECIPIENTS = gql`
  query EfaxRecipientsList {
    efaxRecipientsList {
      totalCount
      items {
        id
        social_program
        firstname
        lastname
        email_recipient
        disabled
        created
        updated
      }
    }
    efaxRecipientsMeta {
      programOptions {
        value
        label
      }
    }
  }
`;

const CREATE_EFAX_RECIPIENT = gql`
  mutation CreateEfaxRecipient($input: EfaxRecipientInput!) {
    createEfaxRecipient(input: $input) {
      id
    }
  }
`;

const UPDATE_EFAX_RECIPIENT = gql`
  mutation UpdateEfaxRecipient($id: Int!, $input: EfaxRecipientInput!) {
    updateEfaxRecipient(id: $id, input: $input) {
      id
    }
  }
`;

const DELETE_EFAX_RECIPIENT = gql`
  mutation DeleteEfaxRecipient($id: Int!) {
    deleteEfaxRecipient(id: $id) {
      message
      responseCode
    }
  }
`;

const TOGGLE_EFAX_RECIPIENT_DISABLED = gql`
  mutation ToggleEfaxRecipientDisabled($id: Int!) {
    toggleEfaxRecipientDisabled(id: $id) {
      id
      disabled
    }
  }
`;

const DEFAULT_PAGE_SIZE = 25;
const DATE_LIKE_VALUE_PATTERN = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
const DEFAULT_PROGRAM_ORDER = ['MIST', 'FIST', 'ODRH Hub 2', 'Both'];

type EfaxRecipient = {
  id: number;
  social_program: string | null;
  firstname: string | null;
  lastname: string | null;
  email_recipient: string | null;
  disabled: boolean;
  created?: string | null;
  updated?: string | null;
};

type EfaxOption = {
  value: string;
  label: string;
};

type EfaxData = {
  efaxRecipientsList: {
    totalCount: number;
    items: EfaxRecipient[];
  };
  efaxRecipientsMeta: {
    programOptions: EfaxOption[];
  };
};

const EMPTY_RECIPIENTS: EfaxRecipient[] = [];
const EMPTY_OPTIONS: EfaxOption[] = [];

type EfaxFormState = {
  id: number | null;
  social_program: string;
  firstname: string;
  lastname: string;
  email_recipient: string;
  disabled: boolean;
};

const emptyFormState: EfaxFormState = {
  id: null,
  social_program: '',
  firstname: '',
  lastname: '',
  email_recipient: '',
  disabled: false,
};

const normalizeText = (value: string) => `${value || ''}`.trim();

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isDateLikeProgramValue = (value: string | null | undefined) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) {
    return false;
  }

  return DATE_LIKE_VALUE_PATTERN.test(normalized);
};
const getProgramDisplayValue = (value: string | null | undefined) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) {
    return '';
  }

  if (isDateLikeProgramValue(normalized)) {
    return 'Legacy';
  }

  return normalized;
};

const getRowStyle = (row: EfaxRecipient): React.CSSProperties => {
  if (row.disabled) {
    return { backgroundColor: '#e5e7eb' };
  }

  return {};
};

const normalizeFormState = (item?: EfaxRecipient | null): EfaxFormState => {
  if (!item) {
    return emptyFormState;
  }

  return {
    id: item.id,
    social_program: isDateLikeProgramValue(item.social_program) ? '' : item.social_program || '',
    firstname: item.firstname || '',
    lastname: item.lastname || '',
    email_recipient: item.email_recipient || '',
    disabled: Boolean(item.disabled),
  };
};

export const EfaxAdminPage = () => {
  const { user, isUserLoading } = useUser();
  const canAccess = hasAnyPolicy(user, ['tools:efax:access~full']);

  const [searchDraft, setSearchDraft] = useState('');
  const [searchText, setSearchText] = useState('');
  const [programFilter, setProgramFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<EfaxFormState>(emptyFormState);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toggleBusyId, setToggleBusyId] = useState<number | null>(null);
  const [pendingDeleteRecipient, setPendingDeleteRecipient] = useState<EfaxRecipient | null>(null);
  const [pendingToggleRecipient, setPendingToggleRecipient] = useState<EfaxRecipient | null>(null);

  const { data, loading, error, refetch } = useQuery<EfaxData>(LIST_EFAX_RECIPIENTS, {
    fetchPolicy: 'cache-and-network',
    skip: !canAccess,
  });

  const [createRecipient] = useMutation(CREATE_EFAX_RECIPIENT);
  const [updateRecipient] = useMutation(UPDATE_EFAX_RECIPIENT);
  const [deleteRecipient] = useMutation(DELETE_EFAX_RECIPIENT);
  const [toggleRecipientDisabled] = useMutation(TOGGLE_EFAX_RECIPIENT_DISABLED);

  const items = data?.efaxRecipientsList?.items ?? EMPTY_RECIPIENTS;
  const rawProgramOptions = data?.efaxRecipientsMeta?.programOptions ?? EMPTY_OPTIONS;
  const programOptions = useMemo(() => {
    const nonDateOptions = rawProgramOptions.filter((option) => {
      const value = `${option.value || ''}`.trim();
      if (!value) {
        return false;
      }

      return !DATE_LIKE_VALUE_PATTERN.test(value);
    });

    if (nonDateOptions.length === 0) {
      return DEFAULT_PROGRAM_ORDER.map((value) => ({ value, label: value }));
    }

    const byValue = new Map(nonDateOptions.map((option) => [option.value, option]));
    const ordered = DEFAULT_PROGRAM_ORDER
      .filter((value) => byValue.has(value))
      .map((value) => byValue.get(value)!);
    const remaining = nonDateOptions.filter((option) => !DEFAULT_PROGRAM_ORDER.includes(option.value));

    return [...ordered, ...remaining];
  }, [rawProgramOptions]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchText.toLowerCase().trim();

    const result = items
      .filter((item) => {
        if (programFilter !== 'all' && item.social_program !== programFilter) {
          return false;
        }

        if (statusFilter === 'enabled' && item.disabled) {
          return false;
        }

        if (statusFilter === 'disabled' && !item.disabled) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          item.social_program || '',
          item.disabled ? 'disabled' : 'enabled',
        ]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((left, right) => {
        const directionFactor = sortDirection === 'asc' ? 1 : -1;

        if (sortKey === 'id') {
          const leftId = Number(left.id || 0);
          const rightId = Number(right.id || 0);
          return leftId === rightId ? 0 : (leftId > rightId ? directionFactor : -directionFactor);
        }

        if (sortKey === 'updated') {
          const leftUpdated = Date.parse(`${left.updated || ''}`) || 0;
          const rightUpdated = Date.parse(`${right.updated || ''}`) || 0;
          return leftUpdated === rightUpdated ? 0 : (leftUpdated > rightUpdated ? directionFactor : -directionFactor);
        }

        const leftValue = `${(left as any)[sortKey] || ''}`.toLowerCase().trim();
        const rightValue = `${(right as any)[sortKey] || ''}`.toLowerCase().trim();
        if (leftValue === rightValue) {
          return 0;
        }
        return leftValue > rightValue ? directionFactor : -directionFactor;
      });

    return result;
  }, [items, programFilter, searchText, sortDirection, sortKey, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const canGoBack = page > 1;
  const canGoNext = page < totalPages;

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  const pagedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * pageSize;
    return filteredItems.slice(start, start + pageSize);
  }, [filteredItems, page, pageSize, totalPages]);

  const setField = (field: keyof EfaxFormState, value: string | boolean) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const openCreateDialog = () => {
    setFormState(emptyFormState);
    setDialogOpen(true);
  };

  const openEditDialog = (item: EfaxRecipient) => {
    setFormState(normalizeFormState(item));
    setDialogOpen(true);
  };

  const handleSearch = () => {
    setSearchText(searchDraft.trim());
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchDraft('');
    setSearchText('');
    setProgramFilter('all');
    setStatusFilter('all');
    setPageSize(DEFAULT_PAGE_SIZE);
    setPage(1);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      toast.success('eFax recipients refreshed');
    } catch (refetchError: any) {
      toast.error(refetchError.message || 'Failed to refresh eFax recipients');
    }
  };

  const buildMutationInput = () => {
    const input = {
      social_program: normalizeText(formState.social_program),
      firstname: normalizeText(formState.firstname),
      lastname: normalizeText(formState.lastname),
      email_recipient: normalizeText(formState.email_recipient),
      disabled: formState.disabled,
    };

    if (!input.social_program) {
      throw new Error('Program is required');
    }

    if (!input.email_recipient) {
      throw new Error('Email is required');
    }

    if (!isValidEmail(input.email_recipient)) {
      throw new Error('Email must be valid');
    }

    return input;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const input = buildMutationInput();

      if (formState.id == null) {
        await createRecipient({ variables: { input } });
        toast.success('eFax recipient created');
      } else {
        await updateRecipient({
          variables: {
            id: formState.id,
            input,
          },
        });
        toast.success('eFax recipient updated');
      }

      setDialogOpen(false);
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError.message || 'Failed to save eFax recipient');
    }
    setIsSaving(false);
  };

  const handleDelete = (item: EfaxRecipient) => {
    setPendingDeleteRecipient(item);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteRecipient) {
      return;
    }

    const recipient = pendingDeleteRecipient;
    setIsDeleting(true);
    try {
      await deleteRecipient({
        variables: { id: recipient.id },
      });
      toast.success('eFax recipient deleted');
      setPendingDeleteRecipient(null);
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError.message || 'Failed to delete eFax recipient');
    }
    setIsDeleting(false);
  };

  const handleToggleDisabled = (item: EfaxRecipient) => {
    setPendingToggleRecipient(item);
  };

  const confirmToggleDisabled = async () => {
    if (!pendingToggleRecipient) {
      return;
    }

    const recipient = pendingToggleRecipient;
    setToggleBusyId(recipient.id);
    try {
      await toggleRecipientDisabled({
        variables: { id: recipient.id },
      });
      toast.success('eFax recipient status updated');
      setPendingToggleRecipient(null);
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError.message || 'Failed to update recipient status');
    }
    setToggleBusyId(null);
  };

  const columns: UnifiedTableColumn<EfaxRecipient>[] = [
    {
      key: 'id',
      header: 'ID',
      field: 'id',
      sortable: true,
      dataType: 'int',
      width: '90px',
    },
    {
      key: 'social_program',
      header: 'Program',
      accessor: (row) => getProgramDisplayValue(row.social_program),
      sortValue: (row) => getProgramDisplayValue(row.social_program),
      sortable: true,
      width: '160px',
    },
    {
      key: 'firstname',
      header: 'First Name',
      field: 'firstname',
      sortable: true,
      width: '160px',
    },
    {
      key: 'lastname',
      header: 'Last Name',
      field: 'lastname',
      sortable: true,
      width: '160px',
    },
    {
      key: 'email_recipient',
      header: 'Email',
      field: 'email_recipient',
      sortable: true,
      width: '260px',
    },
    {
      key: 'disabled',
      header: 'Disabled',
      accessor: (row) => (row.disabled ? 'Yes' : 'No'),
      sortable: true,
      sortValue: (row) => (row.disabled ? 1 : 0),
      width: '110px',
    },
    {
      key: 'updated',
      header: 'Updated',
      field: 'updated',
      sortable: true,
      dataType: 'timestamp',
      width: '190px',
    },
  ];

  if (isUserLoading || (loading && !data)) {
    return <OdrLoadingSpinner message="Loading eFax recipients" />;
  }

  if (!canAccess) {
    return (
      <div className="m-6 rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
        You do not have permission to access eFax recipients.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="text-xs text-[var(--neutral-gray-90)]">
        Showing {pagedItems.length} of {filteredItems.length} recipients
        {error ? ` · ${error.message}` : ''}
      </div>

      <UnifiedTable<EfaxRecipient>
        columns={columns}
        data={pagedItems}
        rowKey={(row) => String(row.id)}
        getRowStyle={(row) => getRowStyle(row)}
        className="h-[66vh]"
        searchEnabled
        clientSearch={false}
        searchValue={searchDraft}
        onSearchValueChange={setSearchDraft}
        onSearchSubmit={handleSearch}
        searchPlaceholder="Search program or status"
        variant="striped"
        density="compact"
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSortChange={(key, direction) => {
          setSortKey(key);
          setSortDirection(direction);
        }}
        clientSort={false}
        showRowActions
        rowActionsHeader="Actions"
        toolbarLeft={(
          <div className="flex flex-wrap items-center gap-y-2 [&>div+div]:ml-4 [&>div+div]:border-l [&>div+div]:border-[var(--neutral-gray-60)] [&>div+div]:pl-4">
            <div className="flex items-center gap-2.5 whitespace-nowrap">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">
                Program
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--neutral-gray-120)]">
                <label className="flex items-center gap-1 whitespace-nowrap">
                  <input
                    type="radio"
                    name="efax-program-filter"
                    checked={programFilter === 'all'}
                    onChange={() => {
                      setProgramFilter('all');
                      setPage(1);
                    }}
                  />
                  All
                </label>
                {programOptions.map((option) => (
                  <label key={option.value} className="flex items-center gap-1 whitespace-nowrap">
                    <input
                      type="radio"
                      name="efax-program-filter"
                      checked={programFilter === option.value}
                      onChange={() => {
                        setProgramFilter(option.value);
                        setPage(1);
                      }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2.5 whitespace-nowrap">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">
                Status
              </div>
              <div className="flex items-center gap-x-3 text-[11px] text-[var(--neutral-gray-120)]">
                <label className="flex items-center gap-1 whitespace-nowrap">
                  <input
                    type="radio"
                    name="efax-status-filter"
                    checked={statusFilter === 'all'}
                    onChange={() => {
                      setStatusFilter('all');
                      setPage(1);
                    }}
                  />
                  All
                </label>
                <label className="flex items-center gap-1 whitespace-nowrap">
                  <input
                    type="radio"
                    name="efax-status-filter"
                    checked={statusFilter === 'enabled'}
                    onChange={() => {
                      setStatusFilter('enabled');
                      setPage(1);
                    }}
                  />
                  Enabled
                </label>
                <label className="flex items-center gap-1 whitespace-nowrap">
                  <input
                    type="radio"
                    name="efax-status-filter"
                    checked={statusFilter === 'disabled'}
                    onChange={() => {
                      setStatusFilter('disabled');
                      setPage(1);
                    }}
                  />
                  Disabled
                </label>
              </div>
            </div>

            <div className="flex items-center">
              <Button variant="ghost" size="sm" onClick={handleResetFilters}>
                Reset
              </Button>
            </div>
          </div>
        )}
        toolbarRight={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Refresh
            </Button>

            <Button size="sm" onClick={openCreateDialog}>
              <Plus className="mr-1.5 h-4 w-4" />
              Add Recipient
            </Button>

            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoBack}
                onClick={() => {
                  if (!canGoBack) {
                    return;
                  }

                  setPage((previousPage) => previousPage - 1);
                }}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <span className="min-w-[80px] text-center text-xs text-[var(--neutral-gray-100)]">
                {page} / {totalPages}
              </span>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={!canGoNext}
                onClick={() => {
                  if (!canGoNext) {
                    return;
                  }

                  setPage((previousPage) => previousPage + 1);
                }}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <select
              value={filteredItems.length > 0 && pageSize >= filteredItems.length ? 'all' : String(pageSize)}
              onChange={(event) => {
                const rawValue = String(event.target.value || '');
                if (rawValue === 'all') {
                  const nextSize = filteredItems.length || DEFAULT_PAGE_SIZE;
                  setPageSize(nextSize);
                  setPage(1);
                  return;
                }

                const nextSize = Number(rawValue || DEFAULT_PAGE_SIZE);
                if (!Number.isFinite(nextSize) || nextSize <= 0) {
                  return;
                }

                setPageSize(nextSize);
                setPage(1);
              }}
              className="h-8 w-[96px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-xs text-[var(--neutral-gray-120)]"
              aria-label="Items per page"
            >
              {[25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}/page
                </option>
              ))}
              <option value="all">All</option>
            </select>
          </div>
        )}
        renderRowActions={(row) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openEditDialog(row)}
              title="Edit recipient"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => handleToggleDisabled(row)}
              disabled={toggleBusyId === row.id}
              aria-label={row.disabled ? 'Enable recipient' : 'Disable recipient'}
              title={row.disabled ? 'Enable recipient' : 'Disable recipient'}
            >
              {row.disabled ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDelete(row)}
              disabled={isDeleting}
              title="Delete recipient"
            >
              <Trash2 className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        )}
        emptyTitle="No eFax recipients found"
        emptyDescription="Adjust filters or add a new recipient."
      />

      <Dialog open={dialogOpen} onOpenChange={(open: boolean) => { if (!isSaving) setDialogOpen(open); }}>
        <DialogContent className="max-w-xl gap-0 overflow-hidden p-0" contentHeight="auto" contentMaxHeight="85vh">
          <DialogHeader className="border-b border-[var(--neutral-gray-30)] px-5 py-4">
            <DialogTitle>{formState.id == null ? 'Add eFax Recipient' : 'Edit eFax Recipient'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 px-5 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>First Name</Label>
                <Input
                  value={formState.firstname}
                  onChange={(event) => setField('firstname', event.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Last Name</Label>
                <Input
                  value={formState.lastname}
                  onChange={(event) => setField('lastname', event.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Program</Label>
                <Select value={formState.social_program} onValueChange={(value) => setField('social_program', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select program" />
                  </SelectTrigger>
                  <SelectContent>
                    {programOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  value={formState.email_recipient}
                  onChange={(event) => setField('email_recipient', event.target.value)}
                  placeholder="name@example.org"
                />
              </div>
            </div>

            <div className="rounded-md border border-[var(--neutral-gray-30)] px-3 py-2">
              <Label className="mb-2 block">Disabled</Label>
              <div className="flex items-center gap-4 text-sm text-[var(--neutral-gray-120)]">
                <label className="flex items-center gap-1.5">
                  <input
                    id="efax-disabled-yes"
                    type="radio"
                    name="efax-disabled"
                    checked={formState.disabled}
                    onChange={() => setField('disabled', true)}
                  />
                  Yes
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    id="efax-disabled-no"
                    type="radio"
                    name="efax-disabled"
                    checked={!formState.disabled}
                    onChange={() => setField('disabled', false)}
                  />
                  No
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-[var(--neutral-gray-30)] px-5 py-4">
            <Button onClick={handleSave} disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <YesNoDialog
        open={pendingDeleteRecipient !== null}
        onOpenChange={(open: boolean) => {
          if (open) {
            return;
          }
          setPendingDeleteRecipient(null);
        }}
        title="Delete eFax Recipient"
        description={`Delete eFax recipient "${pendingDeleteRecipient?.email_recipient || pendingDeleteRecipient?.id || ''}"?`}
        onConfirm={confirmDelete}
        confirmDisabled={isDeleting || pendingDeleteRecipient === null}
        hideCloseButton
      />

      <YesNoDialog
        open={pendingToggleRecipient !== null}
        onOpenChange={(open: boolean) => {
          if (open) {
            return;
          }
          setPendingToggleRecipient(null);
        }}
        title={pendingToggleRecipient?.disabled ? 'Enable eFax Recipient' : 'Disable eFax Recipient'}
        description={`Do you want to ${pendingToggleRecipient?.disabled ? 'enable' : 'disable'} this eFax recipient?`}
        onConfirm={confirmToggleDisabled}
        confirmDisabled={pendingToggleRecipient == null || toggleBusyId === pendingToggleRecipient.id}
        hideCloseButton
      />
    </div>
  );
};

export default EfaxAdminPage;
